// What a claim's rejection says — for the screens that report it and the guard
// that acts on it.
//
// Rejection is two steps: `Rejected_Pending` is a rejection still open to being
// reconfirmed or sent back for correction, `Rejected` is the final decision. Both
// record the reason on the claim's own `rejection_reason` column, so one reader
// serves the claim file and the policy's claims card.
//
// Matching is on substrings of the lowercased status rather than the enum values,
// because partners spell these differently ("Declined", "Rejection Pending") and a
// status nobody recognises must not read as a rejection.
//
// No optional chaining / ES6+ syntax the project's Babel config rejects.

function normalise(status) {
  return String(status || '').trim().toLowerCase();
}

/** Was this claim turned down, whether or not the rejection is final? */
export function isAnyRejection(status) {
  const s = normalise(status);
  return s.indexOf('reject') !== -1 || s.indexOf('declin') !== -1;
}

/** A rejection still awaiting confirmation, so not yet the last word. */
export function isPendingRejection(status) {
  return isAnyRejection(status) && normalise(status).indexOf('pending') !== -1;
}

/** A rejection that stands: the claim has been turned down for good. */
export function isFinalRejection(status) {
  return isAnyRejection(status) && normalise(status).indexOf('pending') === -1;
}

/**
 * Why this claim was rejected, or null when it was not.
 *
 * Gated on the status on purpose: a claim rejected, reconfirmed and then approved
 * keeps the reason on the record, and showing it against an approved claim would
 * read as a rejection that never happened.
 */
export function rejectionReasonFromClaim(claim) {
  if (!claim || typeof claim !== 'object' || !isAnyRejection(claim.status)) {
    return null;
  }
  const details = claim.claim_details || {};
  const additional = details.additional_details || {};
  const reason = claim.rejection_reason || additional.rejection_reason;
  return reason ? String(reason) : null;
}
